<template>
  <TicketCreateEditStepper
    :show="show"
    mode="create"
    title="Report New Bug"
    subtitle="Fill in the details to report a new bug."
    ticket-type="bug"
    :step="step"
    :saving="saving"
    :disable-next="disableNext"
    :disable-submit="disableSubmit"
    submit-text="Create Bug"
    @update:show="onUpdateShow"
    @close="close"
    @update:step="step = $event"
    @submit="submit"
  >
    <template #ticket-properties>
      <BugForm
        ref="bugFormStep0Ref"
        v-model="form"
        v-model:attachments="attachmentList"
        :users="users"
        :features="features"
        :test-cases="testCases"
        :step="0"
        :disabled="saving"
        @update:compressing="isCompressing = $event"
        @validity="onValidity"
      />
    </template>
    <template #ticket-info>
      <BugForm
        ref="bugFormStep1Ref"
        v-model="form"
        v-model:attachments="attachmentList"
        :users="users"
        :features="features"
        :test-cases="testCases"
        :step="1"
        :disabled="saving"
        @update:compressing="isCompressing = $event"
        @validity="onValidity"
      />
    </template>
    <template #links>
      <BugForm
        ref="bugFormStep2Ref"
        v-model="form"
        v-model:attachments="attachmentList"
        :users="users"
        :features="features"
        :test-cases="testCases"
        :step="2"
        :disabled="saving"
        @update:compressing="isCompressing = $event"
        @validity="onValidity"
      />
    </template>
  </TicketCreateEditStepper>
</template>

<script setup>
  import { computed, onBeforeMount, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import TicketCreateEditStepper from '@/components/tickets/TicketCreateEditStepper.vue'
  import BugForm from '@/components/bugs/BugForm.vue'
  import { bugService } from '@/database/services/BugService'
  import { featureService } from '@/database/services/FeatureService'
  import { testCaseService } from '@/database/services/TestCaseService'
  import { useAuthStore } from '@/stores/auth'
  import { useUserStore } from '@/stores/user'
  import { useToast } from '@/composables/useToast'
  import { uploadPendingBugAttachments } from '@/utils/bugAttachments'
  import { trackCreateFlowCompleted, trackCreateFlowFailed } from '@/telemetry/workflowTelemetry'

  const props = defineProps({
    show: { type: Boolean, default: false },
    projectId: { type: String, default: '' },
    naivigateToBug: { type: Boolean, default: true },
  })

  const emit = defineEmits(['update:show', 'created'])

  const router = useRouter()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const toast = useToast()

  const getDefaultForm = () => ({
    title: '',
    status: 'open',
    priority: 'medium',
    severity: 'moderate',
    type: 'bug',
    assigned_to: authStore.user?.id || authStore.user?.uid || '',
    environment: 'versioned_staging',
    test_data: '',
    steps_to_reproduce: '',
    expected_result: '',
    actual_result: '',
    attachments: '',
    feature_ids: [],
    test_case_ids: [],
  })

  const form = ref(getDefaultForm())
  const attachmentList = ref([])
  const step = ref(0)
  const saving = ref(false)
  const isCompressing = ref(false)

  const users = ref([])
  const features = ref([])
  const testCases = ref([])

  const bugFormStep0Ref = ref(null)
  const bugFormStep1Ref = ref(null)
  const bugFormStep2Ref = ref(null)

  const activeBugFormRef = () => bugFormStep0Ref.value || bugFormStep1Ref.value || bugFormStep2Ref.value

  const validity = ref({ step0Valid: false, step1Valid: false, submitValid: false })
  const onValidity = v => {
    validity.value = v
  }

  const disableNext = computed(() => {
    if (step.value === 0) return !validity.value.step0Valid
    if (step.value === 1) return !validity.value.step1Valid
    return false
  })
  const disableSubmit = computed(() => !validity.value.submitValid || isCompressing.value)

  const loadUsers = async () => {
    try {
      const data = await userStore.fetchUsers()
      users.value = data || []
    } catch {
      users.value = []
    }
  }

  onBeforeMount(() => {
    loadUsers()
  })

  const normalizeFeatures = data =>
    (data || []).map(f => {
      if (f.id && f.id.match(/^F\d+$/)) {
        return { ...f, id: f._id || f.id, featureId: f.id }
      }
      return {
        ...f,
        id: f.id || f._id || f.featureId,
        parent_id: f.parent_id || f.parentId || null,
        sub_task_ids: f.sub_task_ids || [],
      }
    })

  const loadFeatures = async () => {
    if (!props.projectId) {
      features.value = []
      return
    }
    try {
      const data = await featureService.getByProject(props.projectId)
      features.value = normalizeFeatures(data)
    } catch {
      features.value = []
    }
  }

  const loadTestCases = async () => {
    if (!props.projectId) {
      testCases.value = []
      return
    }
    try {
      const data = await testCaseService.getByProject(props.projectId)
      testCases.value = (data || []).filter(tc => !tc.is_deleted)
    } catch {
      testCases.value = []
    }
  }

  const reset = () => {
    form.value = getDefaultForm()
    attachmentList.value = []
    step.value = 0
    activeBugFormRef()?.resetValidation?.()
  }

  watch([() => props.show, () => props.projectId], ([visible, projectId], [wasVisible] = []) => {
    if (!visible) return
    if (!wasVisible) reset()
    if (projectId) {
      loadFeatures()
      loadTestCases()
    }
  })

  const onUpdateShow = value => {
    emit('update:show', value)
  }

  const close = () => {
    emit('update:show', false)
  }

  const syncFeatureLinks = async ({ bugId, nextFeatureIds }) => {
    await Promise.all(
      (nextFeatureIds || []).map(async featureId => {
        const feature = await featureService.getById(featureId)
        if (!feature) return
        const ids = Array.isArray(feature.bug_ids) ? feature.bug_ids : []
        if (ids.includes(bugId)) return
        await featureService.update(featureId, { bug_ids: [...ids, bugId] })
      })
    )
  }

  const syncTestCaseLinks = async ({ bugId, nextTestCaseIds, userId }) => {
    await Promise.all((nextTestCaseIds || []).map(testCaseId => testCaseService.linkBug(testCaseId, bugId, userId)))
  }

  const submit = async () => {
    if (saving.value) return
    if (disableSubmit.value) {
      activeBugFormRef()?.validateAll?.()
      return
    }
    if (!props.projectId) {
      toast.error('No project selected')
      return
    }

    saving.value = true
    try {
      const currentUser = authStore.user
      const currentUserId = currentUser?.id || currentUser?.uid
      const currentUserName = currentUser?.name || currentUser?.email || 'Unknown User'

      const normalized = {
        ...form.value,
        steps_to_reproduce: form.value.steps_to_reproduce.split('\n').filter(s => s.trim().length > 0),
        attachments: form.value.attachments || '',
        updated_at: new Date(),
        updated_by: currentUserId,
        updated_by_name: currentUserName,
        feature_ids: form.value.feature_ids,
        test_case_ids: form.value.test_case_ids,
      }

      const bugId = await bugService.getNextBugId()
      const createdBug = await bugService.createBug({
        id: `bug_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        bugId,
        ...normalized,
        project_id: props.projectId,
        created_by: currentUserId,
        created_by_name: currentUserName,
        created_at: new Date(),
      })

      await syncTestCaseLinks({
        bugId: createdBug.id,
        nextTestCaseIds: form.value.test_case_ids,
        userId: currentUserId,
      })
      await syncFeatureLinks({
        bugId: createdBug.id,
        nextFeatureIds: form.value.feature_ids,
      })
      const uploadedAttachmentCount = await uploadPendingBugAttachments(createdBug.id, attachmentList.value, msg => toast.error(msg))

      toast.success('Bug created')
      trackCreateFlowCompleted('bug', {
        project_id: props.projectId,
        entity_id: createdBug.id,
        source: 'create_bug_modal',
        selected_test_case_count: form.value.test_case_ids.length,
        linked_feature_count: form.value.feature_ids.length,
        attachment_count: uploadedAttachmentCount || 0,
        navigate_to_bug: props.naivigateToBug,
      })
      emit('created', createdBug)
      emit('update:show', false)
      if (props.naivigateToBug) {
        router.push(`/projects/${props.projectId}/bugs/${createdBug.id}`)
      }
    } catch (error) {
      trackCreateFlowFailed('bug', {
        project_id: props.projectId,
        source: 'create_bug_modal',
        reason: error?.response?.data?.error || error?.message || 'unknown',
      })
      toast.error(error?.response?.data?.error || error?.message || 'Failed to create bug')
    } finally {
      saving.value = false
    }
  }
</script>
